'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useMemo, createRef, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import styled, { css } from 'styled-components';
import { withTitan } from '../../hocs/withTitan/withTitan.js';

const transparentPlaceholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
const ImageContainer = styled.picture.withConfig({
  componentId: "sc-3zx9jm-0"
})(["position:relative;display:block;", " ", " ", ""], ({
  ratio
}) => ratio && css(["padding-top:", ";"], `${ratio * 100}%`), ({
  width
}) => width && css(["width:", ";"], typeof width === 'number' ? `${width}px` : width), ({
  height
}) => height && css(["height:", ";"], typeof height === 'number' ? `${height}px` : height));
const StyledImg = styled.img.withConfig({
  componentId: "sc-3zx9jm-1"
})(["display:block;width:100%;height:100%;transition:opacity 300ms ease-in-out;opacity:", ";object-fit:", ";", " ", " ", ""], ({
  isVisible
}) => isVisible ? 1 : 0, ({
  objectFit
}) => objectFit, ({
  styles
}) => styles, ({
  objectPosition
}) => objectPosition && css(["object-position:", ";"], objectPosition), ({
  hasRatio
}) => hasRatio && css(["position:absolute;top:0;left:0;"]));
const Image = /*#__PURE__*/withTitan(({
  width,
  height,
  src,
  sources = [],
  alt,
  placeholderSrc,
  objectFit = 'cover',
  onLoad,
  lazyload = true,
  objectPosition,
  onError,
  ratio,
  styles,
  testId = 'image'
}) => {
  const isMounted = useRef(true);
  const sourcesRef = useRef([]);
  const [hasLoaded, setHasLoaded] = useState(true);
  const [ref, isInView] = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: '100px'
  });
  sourcesRef.current = useMemo(() => sources.map(_ref => createRef()), [sources]);
  useEffect(() => {
    const children = sourcesRef.current;
    if (!children.length) {
      return;
    }
    if (lazyload && !isInView) {
      return;
    }
    children.forEach((item, index) => {
      var _a, _b, _c;
      const child = item.current;
      if (!child) return;
      if (child.hasAttribute('data-srcset')) {
        // set srcSet on child
        const srcset = (_b = (_a = sources[index]) === null || _a === void 0 ? void 0 : _a.srcset) === null || _b === void 0 ? void 0 : _b.join(', ');
        if (srcset) child.setAttribute('srcset', srcset);
      } else {
        // set src on child
        const src = (_c = sources[index]) === null || _c === void 0 ? void 0 : _c.src;
        if (src) child.setAttribute('src', src);
      }
      // Image events
      child.addEventListener('load', event => {
        if (isMounted.current) {
          setHasLoaded(true);
          onLoad === null || onLoad === void 0 ? void 0 : onLoad(event);
        }
      }, false);
      child.addEventListener('error', event => {
        if (isMounted.current) {
          setHasLoaded(false);
          onError === null || onError === void 0 ? void 0 : onError(event);
        }
      }, false);
    });
  }, [isInView, lazyload, sourcesRef, sources, onLoad, onError]);
  return jsxs(ImageContainer, {
    ref: ref,
    "data-test-id": testId,
    height: height,
    ratio: ratio,
    width: width,
    children: [sources.map(({
      type,
      srcset,
      sizes,
      src
    }, index) => jsx("source", {
      ref: sourcesRef.current[index],
      "data-srcset": srcset,
      sizes: sizes,
      src: src,
      srcSet: src,
      type: type
    }, `${type}-${src}-${index}`)), jsx(StyledImg, {
      alt: alt,
      hasRatio: !!ratio,
      isVisible: Boolean(hasLoaded || placeholderSrc),
      objectFit: objectFit,
      objectPosition: objectPosition,
      src: hasLoaded ? src : placeholderSrc || transparentPlaceholder,
      styles: styles
    })]
  });
}, {
  name: 'Image'
});

export { Image };
//# sourceMappingURL=Image.js.map
